import { useEffect, useState } from "react"
import { Loader2, ShieldCheck } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { AuthErrorBanner } from "@/components/auth-field"

export function IPAllowlistPage() {
  const [text, setText] = useState("")
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    let cancelled = false
    fetch("/api/v1/saas/ip-allowlist", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((data: { cidrs?: string[] }) => {
        if (!cancelled) setText((data.cidrs ?? []).join("\n"))
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const save = async () => {
    setSaving(true)
    setError(null)
    setSaved(false)
    const cidrs = text
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean)
    try {
      const res = await fetch("/api/v1/saas/ip-allowlist", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ cidrs }),
      })
      const body = (await res.json().catch(() => ({}))) as { error?: string; cidrs?: string[] }
      if (!res.ok) {
        setError(body.error ?? "Couldn't save the allowlist.")
        return
      }
      setText((body.cidrs ?? []).join("\n"))
      setSaved(true)
    } catch {
      setError("Couldn't reach the server. Try again.")
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
          <ShieldCheck className="h-5 w-5 text-rose-500" />
          IP allowlist
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Restrict ingestion and API-key access to these IPs or CIDR ranges. Leave
          empty to allow all. Your signed-in dashboard session is always allowed, so
          you can't lock yourself out here.
        </p>
      </div>

      <Card>
        <CardContent className="space-y-4 p-6">
          {loading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : (
            <>
              <label className="text-sm font-medium">
                Allowed IPs / CIDRs <span className="text-muted-foreground">(one per line)</span>
              </label>
              <textarea
                value={text}
                onChange={(e) => {
                  setText(e.target.value)
                  setSaved(false)
                }}
                rows={8}
                spellCheck={false}
                placeholder={"203.0.113.4\n198.51.100.0/24"}
                className="w-full rounded-md border border-input bg-transparent p-3 font-mono text-sm outline-none focus:border-ring"
              />
              {error && <AuthErrorBanner message={error} />}
              <div className="flex items-center gap-3">
                <Button
                  onClick={save}
                  disabled={saving}
                  className="bg-rose-600 text-white hover:bg-rose-500 disabled:bg-rose-600/50"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save allowlist"}
                </Button>
                {saved && <span className="text-sm text-emerald-500">Saved.</span>}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
